import React, { useState, useRef, useCallback } from 'react'
import html2canvas from 'html2canvas'
import PptxGenJS from 'pptxgenjs'

/* ━━━ データ定義 ━━━ */

const MARKET_STATS = [
  { num: '73.5%', label: 'AI記事で検索順位向上を実感', src: '2026年 SEO担当者調査' },
  { num: '4.4×', label: 'AI検索経由のCVR（従来SEO比）', src: 'AIO/LLMO市場レポート 2026' },
  { num: '94%', label: 'マーケターがAI記事制作を活用', src: 'Content Marketing Institute' },
  { num: '50%', label: 'AIソースからのクリックは独自データ記事', src: 'Typeface調査 2026' },
]

const TRENDS = [
  { icon: '🤖', title: 'AI検索最適化（AIO/GEO）', desc: 'ChatGPT・Gemini・AI Overviewsに自社が引用される最適化が標準施策に。従来SEOだけでは検索流入の半分を失うリスク', color: '#6366f1' },
  { icon: '🎯', title: 'トピッククラスター戦略', desc: 'キーワード単体→トピック群へ。ピラーページ＋サテライト記事の構造がAI・検索エンジン双方から高評価', color: '#8b5cf6' },
  { icon: '📊', title: 'E-E-A-T × 独自データ', desc: '経験・専門性・権威性・信頼性の4軸評価が厳格化。独自調査・一次情報を持つ記事だけがAIに引用される', color: '#ec4899' },
  { icon: '🔄', title: '継続的コンテンツ改善', desc: '公開して終わりではなく、パフォーマンス追跡→リライト→再公開のサイクルが検索評価を積み上げる鍵', color: '#10b981' },
]

const PAIN_POINTS = [
  { problem: 'キーワード選定が属人的', impact: '担当者の勘に依存。検索ボリュームや競合難易度を考慮せず、成果の出ない記事を量産', color: '#ef4444' },
  { problem: '不足記事が見えない', impact: '自社サイトにどのトピックが欠けているか把握できず、競合にトラフィックを奪われ続ける', color: '#f59e0b' },
  { problem: '記事制作に時間がかかる', impact: '1記事あたり8〜16時間。月4本が限界で、トピックカバレッジが広がらない', color: '#8b5cf6' },
  { problem: 'リライト優先度が不明', impact: '100本以上の既存記事のどれを改善すべきか判断できず、効果の薄い記事が放置される', color: '#ec4899' },
  { problem: 'AI検索への対応が未着手', impact: 'GEO/LLMO対策をしていないため、AI検索結果で競合ブランドだけが推奨される', color: '#3b82f6' },
  { problem: '公開後の効果測定が手動', impact: 'GA/GSCを都度確認。改善サイクルが遅く、タイムリーなリライト判断ができない', color: '#10b981' },
]

const WORKFLOW_STEPS = [
  { num: '01', title: '市場動向分析', icon: '📡', desc: '業界トレンドキーワードを自動検出。検索ボリュームの時系列変化と競合の出稿状況を可視化し、今狙うべき市場を特定', color: '#6366f1', status: 'current' },
  { num: '02', title: 'キーワード推薦', icon: '🎯', desc: '検索数・競合難易度・CVEをAIが総合評価。ワードマップで視覚化し、トピッククラスター単位で狙うべきKWを自動提案', color: '#8b5cf6', status: 'current' },
  { num: '03', title: '不足記事分析', icon: '🔍', desc: '既存コンテンツと推奨トピックのギャップを自動検出。カニバリゼーション・内部リンク構造も診断し、優先制作リストを生成', color: '#a78bfa', status: 'current' },
  { num: '04', title: 'AI記事制作', icon: '✍️', desc: 'キーワード→構成案→本文をAIがワンストップ生成。E-E-A-T準拠チェックと品質スコアで公開レベルの記事を最短30分で', color: '#c084fc', status: 'current' },
  { num: '05', title: 'SEO診断・リライト', icon: '🩺', desc: '公開済み記事を5項目100点で自動診断。インパクト×工数の4象限マトリクスで優先度を判定し、AIがリライト案を生成', color: '#e879f9', status: 'current' },
  { num: '06', title: '再アップロード・公開', icon: '🚀', desc: 'W2特集ページへAPI連携で直接アップロード。公開スケジュール管理とパフォーマンス追跡を一元化', color: '#f472b6', status: 'roadmap' },
]

const CURRENT_FEATURES = [
  {
    category: 'キーワード分析・推薦',
    icon: '🎯',
    color: '#6366f1',
    features: [
      { name: '複数キーワード一括分析', desc: '検索数・CVE・難易度を一括推定' },
      { name: 'ワードマップ可視化', desc: 'X軸:検索数、Y軸:CVE、サイズ:ボリューム' },
      { name: '関連キーワード自動展開', desc: 'Googleサジェストベースで候補を網羅' },
      { name: '子記事候補の自動提案', desc: 'ピラーKWから派生記事を自動生成' },
      { name: '業界トレンドKW自動検出', desc: '急上昇・新出キーワードを自動検出' },
      { name: '検索トレンド時系列', desc: '過去12ヶ月の検索数推移をグラフ表示' },
      { name: '広告出稿分析', desc: '推定CPC・推奨チャネル判定マトリクス' },
    ]
  },
  {
    category: 'AI記事制作',
    icon: '✍️',
    color: '#8b5cf6',
    features: [
      { name: 'AI構成案生成', desc: '見出し構造をSEO最適で自動設計' },
      { name: 'AI本文生成', desc: 'KW→タイトル→構成→本文をワンストップ' },
      { name: 'トーン選択', desc: '記事のトーン&マナーをカスタマイズ' },
      { name: 'CSV出力', desc: '分析結果・構成案をCSVでエクスポート' },
    ]
  },
  {
    category: 'SEO診断・リライト',
    icon: '🩺',
    color: '#ec4899',
    features: [
      { name: '5項目100点SEO診断', desc: 'タイトル・メタ・構造・内部リンク・UX' },
      { name: '一括診断', desc: '複数ページの優先度を4象限マトリクスで判定' },
      { name: 'AIリライト案生成', desc: 'タイトル・メタ・見出しの改善案を自動生成' },
      { name: '記事原稿チェック', desc: '下書きをW2特集ページと比較分析' },
    ]
  },
  {
    category: 'GEO/LLMO最適化',
    icon: '🌐',
    color: '#10b981',
    features: [
      { name: 'サイテーション分析', desc: 'AI検索での自社ブランド引用元を可視化' },
      { name: 'シェア・オブ・ボイス', desc: 'AI回答における自社の言及率を測定' },
      { name: 'センチメント分析', desc: 'AIが語る自社ブランドの評価傾向' },
      { name: 'クエリ・ファンアウト', desc: '会話深化時のブランド持続性を分析' },
    ]
  },
]

const ROADMAP_FEATURES = [
  { icon: '🗂️', title: 'トピッククラスター自動設計', desc: 'ピラーページとサテライト記事の最適構造をAIが自動提案。内部リンク戦略まで一括設計', priority: 'HIGH', color: '#8b5cf6' },
  { icon: '📝', title: 'コンテンツギャップ分析', desc: '自社の既存記事と推奨トピックを照合し、カバーできていないテーマを自動検出。競合との差分も可視化', priority: 'HIGH', color: '#ec4899' },
  { icon: '🚀', title: 'W2特集ページ直接アップロード', desc: 'AI生成記事をW2管理画面からワンクリックで特集ページに公開。HTMLテンプレート自動変換対応', priority: 'HIGH', color: '#f59e0b' },
  { icon: '📅', title: '公開スケジュール管理', desc: '記事の公開日時を予約設定。編集カレンダーでチーム全体の制作進捗を一元管理', priority: 'MID', color: '#10b981' },
  { icon: '📊', title: '公開後パフォーマンス追跡', desc: '公開記事のPV・滞在時間・CVR・検索順位を自動トラッキング。リライト判定と連動した改善サイクル', priority: 'HIGH', color: '#3b82f6' },
  { icon: '🏷️', title: 'E-E-A-T準拠スコアリング', desc: '記事の経験・専門性・権威性・信頼性をAIが自動評価。公開前に品質基準をクリアしているか判定', priority: 'MID', color: '#c084fc' },
]

const EFFECT_METRICS = [
  { label: '記事制作時間', before: '8〜16時間/本', after: '30分〜1時間', improvement: '最大95%短縮', color: '#6366f1' },
  { label: '月間制作本数', before: '3〜4本', after: '20〜30本', improvement: '5〜8倍', color: '#8b5cf6' },
  { label: 'キーワード選定', before: '担当者の勘', after: 'AIデータドリブン', improvement: '属人性ゼロ', color: '#ec4899' },
  { label: 'リライト判断', before: '手動で1本ずつ', after: '一括診断+優先度自動判定', improvement: '工数90%削減', color: '#10b981' },
]

const IMPLEMENTATION_STEPS = [
  { num: '01', title: 'サイト調査・競合分析', duration: '即日', desc: 'ドメイン登録後、AIが競合サイトの流入数・ランクインKW・DA推定を自動分析', color: '#6366f1' },
  { num: '02', title: 'キーワード戦略策定', duration: '1日', desc: '狙うべきキーワード群をワードマップで可視化。トピッククラスターの骨格を設計', color: '#8b5cf6' },
  { num: '03', title: 'AI記事一括生成', duration: '1〜3日', desc: '優先度の高いキーワードからAI構成案→本文を一括生成。品質チェック後に公開', color: '#a78bfa' },
  { num: '04', title: 'SEO診断・リライト', duration: '継続', desc: '既存記事を一括診断し、優先リライト。新規+改善の両輪でトピックカバレッジを拡大', color: '#c084fc' },
  { num: '05', title: 'GEO/LLMO最適化', duration: '継続', desc: 'AI検索での自社ブランド可視化を定期モニタリング。引用率向上のためのコンテンツ戦略を継続改善', color: '#e879f9' },
]

/* ━━━ ページ定義 ━━━ */

const SLIDES = [
  { label: '市場環境', msg: '2026年のコンテンツマーケティング最前線' },
  { label: '課題', msg: 'EC事業者が直面するコンテンツの壁' },
  { label: '解決フロー', msg: 'AI × データで実現するワンストップ運用' },
  { label: '現行機能', msg: '今すぐ使えるMedia-DRIVERプラットフォーム' },
  { label: 'ロードマップ', msg: '開発中の次世代機能' },
  { label: '導入効果', msg: 'Before / After で見る改善インパクト' },
  { label: '導入ステップ', msg: '最短即日で始められる5ステップ' },
  { label: 'GEO/LLMO', msg: 'AI検索時代のブランド可視化戦略' },
]

/* ━━━ コンポーネント ━━━ */

export default function ContentProposal() {
  const [activeSlide, setActiveSlide] = useState(0)
  const [exporting, setExporting] = useState(false)
  const pageRef = useRef(null)

  // PPT保存: 全8ページを画像としてキャプチャしPPTに出力
  const handleExportPPT = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      const pptx = new PptxGenJS()
      pptx.layout = 'LAYOUT_16x9'
      pptx.author = 'W2 Smart Hub'
      pptx.title = 'Media-DRIVER AI Content Marketing'

      const savedSlide = activeSlide
      const container = pageRef.current
      if (!container) return

      // 各ページを順番にキャプチャ
      for (let i = -1; i < SLIDES.length; i++) {
        // i=-1: Heroページ、i=0~7: 各スライド
        if (i >= 0) {
          setActiveSlide(i)
          await new Promise(r => setTimeout(r, 300)) // レンダリング待ち
        }

        const canvas = await html2canvas(container, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          logging: false,
        })

        const imgData = canvas.toDataURL('image/png')
        const slide = pptx.addSlide()
        slide.background = { color: '0f172a' }
        slide.addImage({
          data: imgData,
          x: 0, y: 0, w: 10, h: 5.625,
          sizing: { type: 'contain', w: 10, h: 5.625 },
        })

        // Heroは1回だけ
        if (i === -1) {
          setActiveSlide(0)
          await new Promise(r => setTimeout(r, 300))
        }
      }

      setActiveSlide(savedSlide)
      await pptx.writeFile({ fileName: 'Media-DRIVER_ServiceGuide.pptx' })
    } catch (err) {
      console.error('PPT export error:', err)
      alert('PPT出力に失敗しました: ' + err.message)
    } finally {
      setExporting(false)
    }
  }, [activeSlide, exporting])

  return (
    <div style={s.page} ref={pageRef}>
      {/* ── PPT保存ボタン ── */}
      <div style={s.exportBar}>
        <button onClick={handleExportPPT} disabled={exporting} style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1 }}>
          {exporting ? '⏳ PPT出力中...' : '📥 PPTに保存'}
        </button>
      </div>

      {/* ── Hero ── */}
      <section style={s.hero}>
        <div style={s.heroBadge}>CONTENT MARKETING GUIDE</div>
        <h1 style={s.heroTitle}>
          <span style={s.heroAccent}>Media-DRIVER</span>
          <span style={s.heroTitleSub}>AI Content Marketing</span>
        </h1>
        <p style={s.heroSub}>powered by W2 Smart Hub</p>
        <p style={s.heroDesc}>
          市場動向を読み、狙うべきキーワードをAIが推薦。<br />
          不足記事を特定し、AI記事制作から公開・効果測定までワンストップで実現。
        </p>
        <div style={s.flowPreview}>
          {['市場分析', 'KW推薦', '不足分析', 'AI制作', 'SEO診断', '公開・追跡'].map((step, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div style={s.flowArrow}>→</div>}
              <div style={{ ...s.flowChip, background: `rgba(99,102,241,${0.1 + i * 0.04})`, borderColor: `rgba(99,102,241,${0.2 + i * 0.06})` }}>
                {step}
              </div>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── ページ目次 ── */}
      <section style={s.agendaSection}>
        <h2 style={s.agendaSectionTitle}>コンテンツマーケティング サービス紹介</h2>
        <p style={s.agendaHint}>クリックで各セクションに切り替え</p>
        <div style={s.agendaGrid}>
          {SLIDES.map((sl, i) => {
            const isActive = activeSlide === i
            return (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                style={{ ...s.agendaCard, ...(isActive ? s.agendaCardActive : {}) }}
              >
                <div style={{ ...s.agendaNum, ...(isActive ? s.agendaNumActive : {}) }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div style={s.agendaBody}>
                  <div style={{ ...s.agendaLabel, ...(isActive ? s.agendaLabelActive : {}) }}>{sl.label}</div>
                  <div style={{ ...s.agendaMsg, ...(isActive ? s.agendaMsgActive : {}) }}>{sl.msg}</div>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* ── スライド表示 ── */}
      <div style={s.slideContainer}>
        <div style={s.sectionDivider} />
        <div style={s.sectionHeader}>
          <div style={s.sectionNum}>{String(activeSlide + 1).padStart(2, '0')}</div>
          <div>
            <h2 style={s.sectionTitle}>{SLIDES[activeSlide].label}</h2>
            <p style={s.sectionSub}>{SLIDES[activeSlide].msg}</p>
          </div>
        </div>

        {/* ─ Page 1: 市場環境 ─ */}
        {activeSlide === 0 && (
          <div>
            <h3 style={s.subHeading}>2026年、コンテンツマーケティングの常識が変わった</h3>
            <p style={s.subDesc}>
              AI検索（ChatGPT・Gemini・AI Overviews）の普及により、ユーザーの情報取得行動が激変。
              従来のSEO順位だけでなく、AIに「引用される」コンテンツを作れるかが集客の分岐点に。
            </p>

            <div style={s.statGrid}>
              {MARKET_STATS.map((st, i) => (
                <div key={i} style={s.statCard}>
                  <div style={s.statNum}>{st.num}</div>
                  <div style={s.statLabel}>{st.label}</div>
                  <div style={s.statSrc}>{st.src}</div>
                </div>
              ))}
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 28 }}>押さえるべき4大トレンド</h3>
            <div style={s.trendGrid}>
              {TRENDS.map((t, i) => (
                <div key={i} style={s.trendCard}>
                  <div style={{ ...s.trendIcon, background: `${t.color}22`, color: t.color }}>{t.icon}</div>
                  <div>
                    <div style={{ ...s.trendTitle, color: t.color }}>{t.title}</div>
                    <div style={s.trendDesc}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 2: 課題 ─ */}
        {activeSlide === 1 && (
          <div>
            <h3 style={s.subHeading}>EC事業者のコンテンツ制作、6つの壁</h3>
            <p style={s.subDesc}>
              多くのEC事業者がコンテンツマーケティングの重要性を認識しながらも、以下の課題により成果に繋がっていません。
            </p>
            <div style={s.painGrid}>
              {PAIN_POINTS.map((p, i) => (
                <div key={i} style={s.painCard}>
                  <div style={{ ...s.painDot, background: p.color }} />
                  <div>
                    <div style={s.painTitle}>{p.problem}</div>
                    <div style={s.painDesc}>{p.impact}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={s.alertBanner}>
              <div style={s.alertIcon}>⚠️</div>
              <p style={s.alertText}>
                2026年現在、<strong style={{ color: '#fbbf24' }}>AI検索経由のCVRは従来SEOの4.4倍</strong>。
                GEO/LLMO対策なしでは、最も購買意欲の高いユーザー層を競合に奪われます。
              </p>
            </div>
          </div>
        )}

        {/* ─ Page 3: 解決フロー ─ */}
        {activeSlide === 2 && (
          <div>
            <h3 style={s.subHeading}>ワンストップ運用フロー</h3>
            <p style={s.subDesc}>
              市場分析からキーワード推薦、記事制作、SEO診断、公開までを一気通貫で実現。
              AIとデータの力で、属人的だったコンテンツ運用を仕組み化します。
            </p>
            <div style={s.workflowTimeline}>
              {WORKFLOW_STEPS.map((ws, i) => (
                <div key={i} style={s.workflowItem}>
                  <div style={s.workflowLeft}>
                    <div style={{ ...s.workflowNum, background: ws.color }}>{ws.num}</div>
                    {i < WORKFLOW_STEPS.length - 1 && <div style={{ ...s.workflowLine, background: `linear-gradient(180deg, ${ws.color}, ${WORKFLOW_STEPS[i + 1].color})` }} />}
                  </div>
                  <div style={{ ...s.workflowContent, borderColor: `${ws.color}33` }}>
                    <div style={s.workflowHeader}>
                      <span style={s.workflowIcon}>{ws.icon}</span>
                      <span style={{ ...s.workflowTitle, color: ws.color }}>{ws.title}</span>
                      {ws.status === 'roadmap' && <span style={s.roadmapBadge}>COMING SOON</span>}
                    </div>
                    <p style={s.workflowDesc}>{ws.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 4: 現行機能 ─ */}
        {activeSlide === 3 && (
          <div>
            <h3 style={s.subHeading}>今すぐ利用可能な機能群</h3>
            <p style={s.subDesc}>
              キーワード分析からAI記事生成、SEO診断、GEO/LLMO分析まで。4つのカテゴリ・16機能で記事制作を強力に支援します。
            </p>
            <div style={s.featureCategoryGrid}>
              {CURRENT_FEATURES.map((cat, ci) => (
                <div key={ci} style={{ ...s.featureCategoryCard, borderColor: `${cat.color}33` }}>
                  <div style={s.featureCategoryHeader}>
                    <span style={{ fontSize: 22 }}>{cat.icon}</span>
                    <span style={{ ...s.featureCategoryTitle, color: cat.color }}>{cat.category}</span>
                  </div>
                  <div style={s.featureList}>
                    {cat.features.map((f, fi) => (
                      <div key={fi} style={s.featureItem}>
                        <div style={{ ...s.featureCheck, color: cat.color }}>✓</div>
                        <div>
                          <div style={s.featureName}>{f.name}</div>
                          <div style={s.featureDesc}>{f.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 5: ロードマップ ─ */}
        {activeSlide === 4 && (
          <div>
            <h3 style={s.subHeading}>開発ロードマップ ─ 次世代機能</h3>
            <p style={s.subDesc}>
              お客様の声と市場動向を反映し、コンテンツマーケティングの完全自動化に向けて開発を推進中です。
            </p>
            <div style={s.roadmapGrid}>
              {ROADMAP_FEATURES.map((rf, i) => (
                <div key={i} style={{ ...s.roadmapCard, borderColor: `${rf.color}33` }}>
                  <div style={s.roadmapCardHeader}>
                    <span style={{ fontSize: 20 }}>{rf.icon}</span>
                    <span style={{ ...s.roadmapPriority, background: rf.priority === 'HIGH' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)', color: rf.priority === 'HIGH' ? '#ef4444' : '#f59e0b' }}>
                      {rf.priority}
                    </span>
                  </div>
                  <div style={{ ...s.roadmapTitle, color: rf.color }}>{rf.title}</div>
                  <div style={s.roadmapDesc}>{rf.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Page 6: 導入効果 ─ */}
        {activeSlide === 5 && (
          <div>
            <h3 style={s.subHeading}>Before / After ─ 導入効果</h3>
            <p style={s.subDesc}>
              AI記事制作プラットフォームの導入により、コンテンツ運用のあらゆる指標が劇的に改善します。
            </p>
            <div style={s.effectGrid}>
              {EFFECT_METRICS.map((em, i) => (
                <div key={i} style={{ ...s.effectCard, borderColor: `${em.color}33` }}>
                  <div style={{ ...s.effectLabel, color: em.color }}>{em.label}</div>
                  <div style={s.effectRow}>
                    <div style={s.effectBefore}>
                      <div style={s.effectBeforeLabel}>Before</div>
                      <div style={s.effectBeforeValue}>{em.before}</div>
                    </div>
                    <div style={s.effectArrow}>→</div>
                    <div style={s.effectAfter}>
                      <div style={s.effectAfterLabel}>After</div>
                      <div style={{ ...s.effectAfterValue, color: em.color }}>{em.after}</div>
                    </div>
                  </div>
                  <div style={{ ...s.effectImprovement, background: `${em.color}15`, color: em.color }}>{em.improvement}</div>
                </div>
              ))}
            </div>

            <div style={s.roiBanner}>
              <div style={s.roiIcon}>💰</div>
              <div>
                <div style={s.roiTitle}>ROI試算</div>
                <p style={s.roiText}>
                  月間制作コスト：外注記事 <strong>30万円/月（10本）</strong> → AI制作 <strong>月額利用料のみで30本</strong><br />
                  年間コスト削減効果：<strong style={{ color: '#10b981', fontSize: 18 }}>約240万円</strong>（制作費）＋ 担当者の工数を戦略業務にシフト
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ─ Page 7: 導入ステップ ─ */}
        {activeSlide === 6 && (
          <div>
            <h3 style={s.subHeading}>最短即日で始められる5ステップ</h3>
            <p style={s.subDesc}>
              W2 Smart Hubの管理画面から、特別な技術知識なしでコンテンツマーケティングを開始できます。
            </p>
            <div style={s.implTimeline}>
              {IMPLEMENTATION_STEPS.map((ist, i) => (
                <div key={i} style={s.implItem}>
                  <div style={s.implLeft}>
                    <div style={{ ...s.implNum, background: `linear-gradient(135deg, ${ist.color}, ${ist.color}cc)` }}>{ist.num}</div>
                    {i < IMPLEMENTATION_STEPS.length - 1 && <div style={s.implLine} />}
                  </div>
                  <div style={s.implContent}>
                    <div style={s.implHeader}>
                      <span style={{ ...s.implTitle, color: ist.color }}>{ist.title}</span>
                      <span style={s.implDuration}>{ist.duration}</span>
                    </div>
                    <p style={s.implDesc}>{ist.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={s.ctaBanner}>
              <div style={s.ctaText}>
                <strong style={{ fontSize: 16, color: '#fff' }}>まずはサイト調査から</strong><br />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>ドメインを登録するだけで、AIが競合分析と改善提案を自動生成します</span>
              </div>
            </div>
          </div>
        )}

        {/* ─ Page 8: GEO/LLMO ─ */}
        {activeSlide === 7 && (
          <div>
            <h3 style={s.subHeading}>AI検索時代のブランド可視化戦略</h3>
            <p style={s.subDesc}>
              Google AI Overviews・ChatGPT・Gemini・Perplexityなど、AI検索エンジンで自社ブランドがどう語られているかを可視化。
              Media-DRIVERとGEO/LLMO最適化を連動させることで、従来SEO＋AI検索の両方からトラフィックを獲得します。
            </p>

            <div style={s.geoFlowWrap}>
              <div style={s.geoFlowTitle}>Media-DRIVER × GEO/LLMO 連動サイクル</div>
              <div style={s.geoFlow}>
                {[
                  { icon: '✍️', label: 'AI記事制作', sub: 'E-E-A-T準拠の\n高品質コンテンツ' },
                  { icon: '📊', label: 'サイテーション分析', sub: 'AIがどこから\n情報を引用しているか' },
                  { icon: '🎯', label: 'SOV測定', sub: 'AI回答での\n自社言及率を計測' },
                  { icon: '💬', label: 'センチメント分析', sub: 'AIが語る\n自社の評価傾向' },
                  { icon: '🔄', label: '改善→再制作', sub: '分析結果を元に\n記事を継続改善' },
                ].map((gf, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div style={s.geoFlowArrow}>▶</div>}
                    <div style={s.geoFlowStep}>
                      <div style={s.geoFlowIcon}>{gf.icon}</div>
                      <div style={s.geoFlowLabel}>{gf.label}</div>
                      <div style={s.geoFlowSub}>{gf.sub}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div style={s.geoValueGrid}>
              {[
                { num: '01', title: 'AI上のブランド認知度を数値化', desc: 'ChatGPTやGeminiで自社が何%の確率で推奨されているかを定量把握。記事施策の効果を直接測定', color: '#6366f1' },
                { num: '02', title: '競合との相対評価', desc: '同カテゴリの競合と比較し、AI回答における自社の立ち位置を明確化。差別化ポイントを特定', color: '#ec4899' },
                { num: '03', title: '記事→引用の因果関係を把握', desc: 'どの記事がAI引用につながっているかを分析。高ROIなコンテンツパターンを特定し、制作戦略に反映', color: '#10b981' },
              ].map((gv, i) => (
                <div key={i} style={{ ...s.geoValueCard, borderColor: `${gv.color}33` }}>
                  <div style={{ ...s.geoValueNum, color: gv.color }}>{gv.num}</div>
                  <div style={{ ...s.geoValueTitle, color: gv.color }}>{gv.title}</div>
                  <div style={s.geoValueDesc}>{gv.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── フッター ── */}
      <div style={s.footer}>
        <p style={s.footerText}>Media-DRIVER Content Marketing &mdash; powered by W2 Smart Hub</p>
      </div>
    </div>
  )
}

/* ━━━ スタイル定義 ━━━ */

const s = {
  page: { maxWidth: 960, margin: '0 auto', padding: '0 0 40px', position: 'relative' },

  /* PPT保存ボタン */
  exportBar: {
    display: 'flex', justifyContent: 'flex-end', marginBottom: 12,
  },
  exportBtn: {
    padding: '8px 20px', borderRadius: 10, border: '1px solid rgba(16,185,129,0.3)',
    background: 'rgba(16,185,129,0.1)', color: '#6ee7b7',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.2s',
  },

  /* Hero */
  hero: {
    textAlign: 'center', padding: '48px 32px 36px',
    background: 'linear-gradient(160deg, rgba(16,185,129,0.10) 0%, rgba(99,102,241,0.10) 50%, rgba(139,92,246,0.06) 100%)',
    borderRadius: 24, border: '1px solid rgba(16,185,129,0.15)', marginBottom: 32,
  },
  heroBadge: {
    display: 'inline-block', padding: '4px 18px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
    background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', marginBottom: 16,
  },
  heroTitle: { fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 2px', letterSpacing: '-0.02em' },
  heroAccent: {
    background: 'linear-gradient(135deg, #6ee7b7 0%, #818cf8 50%, #c084fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroTitleSub: {
    display: 'block', fontSize: '0.42em', fontWeight: 500, marginTop: 6,
    color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em',
  },
  heroSub: { fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.35)', margin: '0 0 18px', letterSpacing: '0.06em' },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, margin: '0 0 28px' },
  flowPreview: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap',
  },
  flowChip: {
    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)',
  },
  flowArrow: { color: 'rgba(255,255,255,0.25)', fontSize: 14, fontWeight: 700 },

  /* Agenda */
  agendaSection: { marginBottom: 32 },
  agendaSectionTitle: { fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.85)', textAlign: 'center', margin: '0 0 20px' },
  agendaHint: { textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '0 0 18px' },
  agendaGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  agendaCard: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14,
    border: '1px solid rgba(226,232,240,0.2)', background: 'rgba(255,255,255,0.92)',
    cursor: 'pointer', transition: 'all 0.25s', textAlign: 'left', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  agendaCardActive: {
    background: 'linear-gradient(135deg, #10b981 0%, #6366f1 100%)',
    border: '1px solid rgba(16,185,129,0.5)', boxShadow: '0 8px 30px rgba(16,185,129,0.3)', transform: 'scale(1.02)',
  },
  agendaNum: {
    width: 40, height: 40, borderRadius: 12,
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', color: '#059669',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 900, flexShrink: 0,
  },
  agendaNumActive: { background: 'rgba(255,255,255,0.2)', color: '#fff' },
  agendaBody: { flex: 1, minWidth: 0 },
  agendaLabel: { fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
  agendaLabelActive: { color: '#fff' },
  agendaMsg: { fontSize: 10, color: '#64748b', lineHeight: 1.4 },
  agendaMsgActive: { color: 'rgba(255,255,255,0.75)' },

  /* Section */
  slideContainer: { minHeight: 400 },
  sectionDivider: {
    height: 1, marginBottom: 32,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0.15) 70%, transparent 100%)',
  },
  sectionHeader: { display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 28 },
  sectionNum: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 44, height: 44, borderRadius: 14,
    background: 'linear-gradient(135deg, #10b981 0%, #6366f1 100%)',
    color: '#fff', fontSize: 16, fontWeight: 800, flexShrink: 0,
  },
  sectionTitle: { fontSize: 24, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: '0 0 4px' },
  sectionSub: { fontSize: 14, color: 'rgba(255,255,255,0.45)', margin: 0 },
  subHeading: { fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.75)', margin: '0 0 12px', paddingLeft: 2 },
  subDesc: { fontSize: 13, color: 'rgba(255,255,255,0.45)', margin: '0 0 20px', lineHeight: 1.7 },

  /* Page 1: 市場環境 */
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 8 },
  statCard: {
    padding: '22px 16px', borderRadius: 16, textAlign: 'center',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  },
  statNum: {
    fontSize: 32, fontWeight: 900, lineHeight: 1,
    background: 'linear-gradient(135deg, #6ee7b7 0%, #818cf8 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 8,
  },
  statLabel: { fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 6, lineHeight: 1.4 },
  statSrc: { fontSize: 10, color: 'rgba(255,255,255,0.3)' },
  trendGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  trendCard: {
    display: 'flex', gap: 14, padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  },
  trendIcon: {
    width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 22, flexShrink: 0,
  },
  trendTitle: { fontSize: 14, fontWeight: 700, marginBottom: 6 },
  trendDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },

  /* Page 2: 課題 */
  painGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 },
  painCard: {
    display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 22px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
  },
  painDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  painTitle: { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.85)', margin: '0 0 6px' },
  painDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.6 },
  alertBanner: {
    display: 'flex', alignItems: 'center', gap: 16, padding: '18px 24px',
    background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(239,68,68,0.06) 100%)',
    border: '1px solid rgba(245,158,11,0.2)', borderRadius: 16,
  },
  alertIcon: { fontSize: 24, flexShrink: 0 },
  alertText: { fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7 },

  /* Page 3: 解決フロー */
  workflowTimeline: { display: 'flex', flexDirection: 'column', gap: 0 },
  workflowItem: { display: 'flex', gap: 16 },
  workflowLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 44, flexShrink: 0 },
  workflowNum: {
    width: 44, height: 44, borderRadius: 22, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 800,
  },
  workflowLine: { width: 2, flex: 1, minHeight: 20, opacity: 0.4 },
  workflowContent: {
    flex: 1, padding: '16px 20px', marginBottom: 12, borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  workflowHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  workflowIcon: { fontSize: 20 },
  workflowTitle: { fontSize: 15, fontWeight: 700 },
  workflowDesc: { fontSize: 12, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.7 },
  roadmapBadge: {
    padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700,
    background: 'rgba(245,158,11,0.15)', color: '#fbbf24', letterSpacing: '0.05em',
  },

  /* Page 4: 現行機能 */
  featureCategoryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  featureCategoryCard: {
    padding: '22px', borderRadius: 18,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  featureCategoryHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 },
  featureCategoryTitle: { fontSize: 15, fontWeight: 700 },
  featureList: { display: 'flex', flexDirection: 'column', gap: 12 },
  featureItem: { display: 'flex', gap: 10 },
  featureCheck: { fontSize: 14, fontWeight: 800, flexShrink: 0, marginTop: 1 },
  featureName: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: 2 },
  featureDesc: { fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 },

  /* Page 5: ロードマップ */
  roadmapGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  roadmapCard: {
    padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  roadmapCardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  roadmapPriority: { padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' },
  roadmapTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
  roadmapDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  /* Page 6: 導入効果 */
  effectGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  effectCard: {
    padding: '22px', borderRadius: 18,
    background: 'rgba(255,255,255,0.04)', border: '1px solid', textAlign: 'center',
  },
  effectLabel: { fontSize: 13, fontWeight: 700, marginBottom: 16 },
  effectRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14 },
  effectBefore: {},
  effectBeforeLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 },
  effectBeforeValue: { fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.5)' },
  effectArrow: { fontSize: 20, color: 'rgba(255,255,255,0.25)', fontWeight: 700 },
  effectAfter: {},
  effectAfterLabel: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 },
  effectAfterValue: { fontSize: 16, fontWeight: 800 },
  effectImprovement: { display: 'inline-block', padding: '4px 14px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  roiBanner: {
    display: 'flex', gap: 16, padding: '22px 24px', borderRadius: 18,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(99,102,241,0.06) 100%)',
    border: '1px solid rgba(16,185,129,0.2)',
  },
  roiIcon: { fontSize: 32, flexShrink: 0 },
  roiTitle: { fontSize: 15, fontWeight: 700, color: '#6ee7b7', marginBottom: 8 },
  roiText: { fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0, lineHeight: 1.8 },

  /* Page 7: 導入ステップ */
  implTimeline: { display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 24 },
  implItem: { display: 'flex', gap: 16 },
  implLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 48, flexShrink: 0 },
  implNum: {
    width: 48, height: 48, borderRadius: 24, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 800,
  },
  implLine: { width: 2, flex: 1, minHeight: 16, background: 'rgba(255,255,255,0.1)' },
  implContent: { flex: 1, paddingBottom: 20 },
  implHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  implTitle: { fontSize: 16, fontWeight: 700 },
  implDuration: {
    padding: '2px 12px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)',
  },
  implDesc: { fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: 0, lineHeight: 1.7 },
  ctaBanner: {
    padding: '24px 28px', borderRadius: 18, textAlign: 'center',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(99,102,241,0.12) 100%)',
    border: '1px solid rgba(16,185,129,0.25)',
  },
  ctaText: { margin: 0, lineHeight: 1.8 },

  /* Page 8: GEO/LLMO */
  geoFlowWrap: {
    padding: '24px', borderRadius: 18, marginBottom: 24,
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
  },
  geoFlowTitle: {
    fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginBottom: 20,
  },
  geoFlow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 6, flexWrap: 'wrap' },
  geoFlowStep: { textAlign: 'center', width: 120 },
  geoFlowIcon: {
    width: 48, height: 48, borderRadius: 24, margin: '0 auto 8px',
    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
  },
  geoFlowLabel: { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4 },
  geoFlowSub: { fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4, whiteSpace: 'pre-line' },
  geoFlowArrow: { color: 'rgba(255,255,255,0.2)', fontSize: 14, marginTop: 16 },
  geoValueGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  geoValueCard: {
    padding: '20px', borderRadius: 16,
    background: 'rgba(255,255,255,0.04)', border: '1px solid',
  },
  geoValueNum: { fontSize: 28, fontWeight: 900, marginBottom: 8, opacity: 0.6 },
  geoValueTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
  geoValueDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  /* Footer */
  footer: {
    textAlign: 'center', padding: '32px 0 0', marginTop: 40,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  footerText: { fontSize: 13, color: 'rgba(255,255,255,0.3)', margin: 0 },
}
